<!-- eslint-disable vue/valid-template-root -->
<template>
  <!-- Renderless component -->
</template>
<!-- eslint-enable vue/valid-template-root -->

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { kebabCase, get } from 'lodash-es'
import i18n from '@/i18n'
import { getRouteSuffix, getDotNotation } from '@/modules/activity/components/utils'
import { useActivitiesCount } from '@/composables/useActivitiesCount'
import { session } from '@/composables/useSession'
import { hasWorkspaceRole } from '@/utils/roleUtils'
import type { SidebarConfig } from '../types'

const emit = defineEmits<{
  'update:config': [config: SidebarConfig]
}>()

type ActivityKey = (typeof ACTIVITY_KEYS)[keyof typeof ACTIVITY_KEYS]

const ACTIVITY_KEYS = {
  // Received
  REVIEWS_RECEIVED: 'reviewsReceived',
  EVALUATIONS_RECEIVED: 'evaluationsReceived',
  COMMENTS_RECEIVED: 'commentsReceived',
  REACTIONS_RECEIVED: 'reactionsReceived',
  FEEDBACK_RECEIVED: 'feedbackReceived',

  // Given
  REVIEWS_GIVEN: 'reviewsGiven',
  EVALUATIONS_GIVEN: 'evaluationsGiven',
  COMMENTS_GIVEN: 'commentsGiven',
  REACTIONS_GIVEN: 'reactionsGiven',

  // Disputes
  DISPUTES_ALL: 'disputesAll',
  DISPUTES_RECEIVED: 'disputesReceived',
  DISPUTES_STARTED: 'disputesStarted',
} as const

const GROUP_KEYS = {
  RECEIVED: 'received',
  GIVEN: 'given',
  DISPUTES: 'disputes',
} as const

const router = useRouter()
const route = useRoute()
const activitiesCount = useActivitiesCount()

const isCurrent = (name: string) => {
  return route.name?.toString().startsWith(`activity.${name}`)
}

const getTestId = (routeName: string) => {
  return kebabCase(getRouteSuffix(routeName))
}

const getActivityCount = (itemKey: string) => {
  const activityCount = get(activitiesCount.value, getDotNotation(itemKey))
  if (!activityCount) return undefined
  return activityCount > 99 ? '99+' : activityCount
}

const handleSubnavItemClick = (navItem: string, subnavItem: string) => {
  if (navItem !== 'activity') return

  if (Object.values(ACTIVITY_KEYS).includes(subnavItem as ActivityKey)) {
    router.push({ name: `activity.${subnavItem}` })
  }
}

const toSidenavItem = (key: ActivityKey, labelText: string) => ({
  children: labelText,
  key,
  isCurrent: isCurrent(key),
  dataTestId: getTestId(key),
  view: 'activity',
  badge: getActivityCount(key),
})

const receivedItems = computed(() => {
  const items = []

  const { REVIEWS_RECEIVED, EVALUATIONS_RECEIVED, COMMENTS_RECEIVED, REACTIONS_RECEIVED, FEEDBACK_RECEIVED } =
    ACTIVITY_KEYS

  // Reviews received
  items.push(toSidenavItem(REVIEWS_RECEIVED, i18n.t('activity.subnav.reviews')))

  // Evaluations received
  if (!hasWorkspaceRole('AGENT') && session.features.gradeTheReviewerDev) {
    items.push(toSidenavItem(EVALUATIONS_RECEIVED, i18n.t('activity.subnav.evaluations')))
  }

  // Comments received
  items.push(toSidenavItem(COMMENTS_RECEIVED, i18n.t('activity.subnav.comments')))

  // Reactions received
  items.push(toSidenavItem(REACTIONS_RECEIVED, i18n.t('activity.subnav.reactions')))

  // Feedback received
  items.push(toSidenavItem(FEEDBACK_RECEIVED, i18n.t('activity.subnav.feedback')))

  return items
})

const givenItems = computed(() => {
  const items = []
  const { REVIEWS_GIVEN, EVALUATIONS_GIVEN, COMMENTS_GIVEN } = ACTIVITY_KEYS

  // Reviews given
  if (!hasWorkspaceRole('AGENT') || session.workspace?.settings?.selfReviews) {
    items.push(toSidenavItem(REVIEWS_GIVEN, i18n.t('activity.subnav.reviews')))
  }

  // Evaluations given
  if (hasWorkspaceRole('MANAGER', 'LEAD') && session.features.gradeTheReviewerDev) {
    items.push(toSidenavItem(EVALUATIONS_GIVEN, i18n.t('activity.subnav.evaluations')))
  }

  // Comments given
  items.push(toSidenavItem(COMMENTS_GIVEN, i18n.t('activity.subnav.comments')))

  return items
})

const disputesItems = computed(() => {
  const items = []
  const { DISPUTES_ALL, DISPUTES_RECEIVED, DISPUTES_STARTED } = ACTIVITY_KEYS

  if (session.features.disputes) {
    // All disputes
    items.push(toSidenavItem(DISPUTES_ALL, i18n.t('activity.subnav.all')))

    // Disputes received
    if (!hasWorkspaceRole('AGENT')) {
      items.push(toSidenavItem(DISPUTES_RECEIVED, i18n.t('activity.subnav.received')))
    }

    // Disputes started
    items.push(toSidenavItem(DISPUTES_STARTED, i18n.t('activity.subnav.started')))
  }

  return items
})

const items = computed(() => [
  {
    key: GROUP_KEYS.RECEIVED,
    legend: i18n.t('activity.subnav.received'),
    items: receivedItems.value,
    dataTestId: 'activity-received-group',
    view: 'activity',
  },
  {
    key: GROUP_KEYS.GIVEN,
    legend: i18n.t('activity.subnav.given'),
    items: givenItems.value,
    dataTestId: 'activity-given-group',
    view: 'activity',
  },
  {
    key: GROUP_KEYS.DISPUTES,
    legend: i18n.t('activity.subnav.disputes'),
    items: disputesItems.value,
    dataTestId: 'activity-disputes-group',
    view: 'activity',
  },
])

const config = computed(
  (): SidebarConfig => ({
    action: {
      tooltip: '',
      menuItems: [],
      onClick: () => {
        /* no-op */
      },
    },
    title: i18n.t('routes.activity.type'),
    items: items.value,
    handleSubnavItemClick,
    handleSubnavItemActionClick: () => {
      /* no-op */
    },
    handleGroupActionClick: () => {
      /* no-op */
    },
  }),
)

// Emit initial config and watch for changes
onMounted(() => {
  emit('update:config', config.value)
})

// Watch for changes and emit updates
watch(config, (newConfig) => {
  emit('update:config', newConfig)
})
</script>
